import { IconFromSet } from "@gettactic/components/src/icons/IconFromSet/IconFromSet";
import { TacticIconsDictionary } from "@gettactic/components/src/icons/tactic-icons/dictionary";
import { generateNextSpotLabels } from "@gettactic/map-builder/src/components/map/helpers/generateNextSpotLabels";
import type { SpotShape, TextShape } from "@gettactic/map-builder/src/types";
import {
  createFurnitureElement,
  createIconElement,
  createNodeShape,
  createText
} from "@gettactic/map-builder/src/utils/shapeFactory";
import { loadSvgFile } from "@gettactic/map-builder/src/utils/svg";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { isPrimaryButton } from "../gesture/pointer";
import type { GhostShape } from "../render/overlayChannel";
import type { ToolRegistryOptions } from "./registry";
import type { Tool, ToolContext } from "./Tool";

/** Click to place a text block; the select tool edits it afterwards. */
export const createTextTool = (): Tool => ({
  kind: "text",
  cursor: "text",
  onPointerDown(context, event) {
    if (!isPrimaryButton(event)) return;
    // Clicking existing text opens it for editing instead of adding more.
    const hitId = context.hitShape(event.target);
    const hit = hitId ? context.store.getState().shapes[hitId] : undefined;
    if (hit?.type === "text") {
      context.store.getState().setSelection([hit.id]);
      context.store
        .getState()
        .setChrome({ highlightedShape: { shape: hit, targetId: hit.id } });
      return;
    }
    const point = context.toWorld({ x: event.clientX, y: event.clientY });
    const text = createText(point) as TextShape;
    text.text = "Text";
    text.layerType = "spots";
    context.store.getState().addShapes([text], { select: true });
  },
  onPointerMove: () => {},
  onPointerUp: () => {},
  cancel: () => {}
});

const followPointer =
  (kind: GhostShape["kind"]) => (context: ToolContext, event: PointerEvent) => {
    const point = context.toWorld({ x: event.clientX, y: event.clientY });
    context.overlay.set({ ghost: { kind, x: point.x, y: point.y } });
  };

/** Click to place a desk with the next label from the map's pattern. The
 *  tool stays active so a row of desks is a row of clicks (restricted mode
 *  places one and returns to select). */
export const createSpotTool = (options: ToolRegistryOptions): Tool => ({
  kind: "spot",
  cursor: "copy",
  onPointerDown(context, event) {
    if (!isPrimaryButton(event)) return;
    const { store } = context;
    const point = context.snap.snapPoint(
      context.toWorld({ x: event.clientX, y: event.clientY })
    );
    const { meta } = store.getState();
    const nextLabel = generateNextSpotLabels(
      meta.spotLabelPattern,
      meta.lastSpotLabel ?? []
    );
    const spot = createNodeShape({
      ...point,
      text: (nextLabel ?? []).join("")
    }) as SpotShape;
    spot.layerType = "spots";
    store.getState().addShapes([spot], { select: options.isRestrictedMode() });
    if (nextLabel) store.getState().setMeta({ lastSpotLabel: nextLabel });
    if (options.isRestrictedMode()) store.getState().setTool("select");
  },
  onPointerMove: followPointer("spot"),
  onPointerUp: () => {},
  cancel: (context) => context.overlay.set({ ghost: null })
});

/**
 * Places the icon picked in the tools dock. The icon's own markup is
 * trimmed to its drawn bounds first, so the placed shape has no padding.
 */
export const createIconTool = (options: ToolRegistryOptions): Tool => ({
  kind: "icon",
  cursor: "copy",
  onPointerDown(context, event) {
    if (!isPrimaryButton(event)) return;
    const { store } = context;
    const icon = store.getState().chrome.iconToolSelectedIcon;
    if (!icon) return;
    const point = context.toWorld({ x: event.clientX, y: event.clientY });

    let iconText = "";
    let iconUrl: string | undefined;
    const apiIcon = options
      .getMapIcons()
      .find((entry) => entry.name === icon.icon && entry.category === "icon");
    if (apiIcon) {
      iconUrl = apiIcon.icon_url;
      iconText = icon.icon.toUpperCase();
    } else if (
      icon.set === "tactic" &&
      icon.icon in TacticIconsDictionary.tactic.icons
    ) {
      iconText = (
        TacticIconsDictionary.tactic.icons?.[icon.icon] || ""
      ).toUpperCase();
    }

    const place = (width?: number, height?: number, svgContent?: string) => {
      const shape = createIconElement(
        point,
        icon,
        iconText,
        iconUrl,
        width,
        height,
        svgContent
      );
      shape.layerType = "shapes";
      store.getState().addShapes([shape]);
    };

    if (iconUrl) {
      fetch(iconUrl)
        .then((response) => response.text())
        .then((markup) => trimAndPlace(markup, place))
        .catch(() => place());
      return;
    }
    try {
      const markup = ReactDOMServer.renderToStaticMarkup(
        React.createElement(IconFromSet, { icon, size: 100 })
      );
      trimAndPlace(markup, place);
    } catch {
      place();
    }
  },
  onPointerMove: followPointer("icon"),
  onPointerUp: () => {},
  cancel: (context) => context.overlay.set({ ghost: null })
});

/** Sizes the placed icon from the markup's drawn bounds rather than its
 *  declared viewBox, which usually carries built-in padding. */
const trimAndPlace = (
  markup: string,
  place: (width?: number, height?: number, svgContent?: string) => void
) => {
  const host = document.createElement("div");
  host.style.cssText =
    "position:absolute;left:-10000px;top:-10000px;visibility:hidden;pointer-events:none;";
  try {
    host.innerHTML = markup;
    document.body.appendChild(host);
    const svg = host.querySelector("svg");
    if (!svg) {
      place();
      return;
    }
    const box = svg.getBBox();
    if (box.width <= 0 || box.height <= 0) {
      place();
      return;
    }
    svg.setAttribute("viewBox", `${box.x} ${box.y} ${box.width} ${box.height}`);
    svg.removeAttribute("width");
    svg.removeAttribute("height");
    const scale = 100 / Math.max(box.width, box.height);
    place(box.width * scale, box.height * scale, svg.outerHTML);
  } catch {
    place();
  } finally {
    if (host.parentNode) document.body.removeChild(host);
  }
};

/** Places the furniture picked in the tools dock, sized from the asset's
 *  own aspect when the catalogue dimensions disagree with it. */
export const createFurnitureTool = (): Tool => ({
  kind: "furniture",
  cursor: "copy",
  onPointerDown(context, event) {
    if (!isPrimaryButton(event)) return;
    const { store } = context;
    const furniture = store.getState().chrome.furnitureToolSelectedFurniture;
    if (!furniture) return;
    const point = context.toWorld({ x: event.clientX, y: event.clientY });
    const place = (width: number, height: number) => {
      const shape = createFurnitureElement(point, furniture);
      shape.layerType = "shapes";
      shape.width = width;
      shape.height = height;
      store.getState().addShapes([shape]);
    };
    const catalogueWidth = furniture.width;
    const catalogueHeight = furniture.height;
    if (!furniture.url) {
      place(catalogueWidth || 100, catalogueHeight || 100);
      return;
    }
    loadSvgFile(furniture.url)
      .then(({ width, height }) => {
        if (!(width > 0 && height > 0)) {
          place(catalogueWidth || 100, catalogueHeight || 100);
          return;
        }
        const naturalAspect = width / height;
        const catalogueAspect =
          catalogueWidth && catalogueHeight
            ? catalogueWidth / catalogueHeight
            : naturalAspect;
        if (
          catalogueWidth &&
          catalogueHeight &&
          Math.abs(catalogueAspect - naturalAspect) <= 0.05
        ) {
          place(catalogueWidth, catalogueHeight);
          return;
        }
        const target =
          catalogueWidth && catalogueHeight
            ? Math.max(catalogueWidth, catalogueHeight)
            : Math.max(width, height);
        const scale = target / Math.max(width, height);
        place(Math.round(width * scale), Math.round(height * scale));
      })
      .catch(() => place(catalogueWidth || 100, catalogueHeight || 100));
  },
  onPointerMove: followPointer("furniture"),
  onPointerUp: () => {},
  cancel: (context) => context.overlay.set({ ghost: null })
});

/** First click picks the source desk; each later click copies its style
 *  onto the clicked desk. */
export const createNodeStyleTool = (): Tool => ({
  kind: "node-style",
  cursor: "cell",
  onPointerDown(context, event) {
    if (!isPrimaryButton(event)) return;
    const { store } = context;
    const id = context.hitShape(event.target);
    const shape = id ? store.getState().shapes[id] : undefined;
    if (shape?.type !== "spot") return;
    const source = store.getState().chrome.nodeStyleToolTarget;
    if (!source || source.id === shape.id) {
      store.getState().setChrome({ nodeStyleToolTarget: shape });
      store.getState().setSelection([shape.id]);
      return;
    }
    const current = store.getState().shapes[source.id];
    if (current?.type !== "spot") {
      store.getState().setChrome({ nodeStyleToolTarget: shape });
      return;
    }
    store.getState().updateShape(shape.id, {
      ...current,
      id: shape.id,
      x: shape.x,
      y: shape.y,
      textProps: { ...current.textProps, text: current.label }
    });
  },
  onPointerMove: () => {},
  onPointerUp: () => {},
  cancel: (context) =>
    context.store.getState().setChrome({ nodeStyleToolTarget: null })
});
